/**
 * <thumbnail-scroll-button>
 *
 * Renders a scroll control beneath the vertical product thumbnail strip.
 * Clicking it scrolls the thumbnails container down by roughly one viewport
 * and loops back to the top once the bottom is reached.
 *
 * Behaviour:
 * - Hidden entirely when the strip doesn't overflow (nothing to scroll).
 * - The arrow points down while there is more to scroll, and flips to point
 *   up once the strip is scrolled to the bottom (next click loops to top).
 *
 * It never touches the main product image — only the thumbnail list scrolls.
 */
class ThumbnailScrollButton extends HTMLElement {
  /** @type {HTMLElement | null} */
  #container = null;
  /** @type {HTMLButtonElement | null} */
  #button = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    this.#button = this.querySelector('button');
    this.#container = this.#findContainer();
    if (!this.#button || !this.#container) return;

    this.#button.addEventListener('click', this.#handleClick);
    this.#container.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this.#container);
    this.#update();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#handleClick);
    this.#container?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  /** Resolves the scrollable thumbnails container this button controls. */
  #findContainer() {
    const controls = this.closest('slideshow-controls');
    const container = controls?.querySelector('.slideshow-controls__thumbnails-container');
    if (container instanceof HTMLElement) return container;
    return this.previousElementSibling instanceof HTMLElement ? this.previousElementSibling : null;
  }

  /** Maximum vertical scroll distance of the container (0 when it doesn't overflow). */
  #maxScroll() {
    const el = this.#container;
    return el ? el.scrollHeight - el.clientHeight : 0;
  }

  #handleClick = (event) => {
    event.preventDefault();
    const el = this.#container;
    if (!el) return;

    const max = this.#maxScroll();
    if (max <= 1) return;

    // Already at (or within a pixel of) the bottom — loop back to the top.
    if (el.scrollTop >= max - 1) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Advance by ~80% of the visible height so a row stays in view for context.
      el.scrollBy({ top: Math.round(el.clientHeight * 0.8), behavior: 'smooth' });
    }
  };

  #update = () => {
    const el = this.#container;
    if (!el || !this.#button) return;

    const max = this.#maxScroll();
    // Hide the control entirely when there's nothing to scroll.
    this.hidden = max <= 1;

    // Flip the arrow to point up once the strip is scrolled to the bottom.
    const atEnd = max > 1 && el.scrollTop >= max - 1;
    this.#button.classList.toggle('slideshow-controls__thumbnail-scroll-button--up', atEnd);
  };
}

if (!customElements.get('thumbnail-scroll-button')) {
  customElements.define('thumbnail-scroll-button', ThumbnailScrollButton);
}
